import { useEffect, useState } from 'react';
import { ControlLabel, Modal } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';

import Select from '../generic/Select/Select';
import { RoleScope } from '../../../typings/enums';
import { roleOptions } from '../../helpers/role';
import RolesModules from '../../redux/roles';
import TeamsModule from '../../redux/teams';
import UsersModule from '../../redux/users';
import OrganizationsModule from '../../redux/organizations';
import { hasError, selectOptionFactory } from '../../utils';
import Button, { ButtonGroup } from '../generic/Button';
import { ErrorAlert } from '../generic/ErrorAlert';
import Loader from '../generic/Loader';
import type { SelectOption, Team } from '../../types';
import { useOnMount } from '../../hooks';

type Props = {
  team: Team;
  onAddedUsers(users: Array<{ id: number; roleId: number }>): void;
  onCancel(): void;
};

/**
 * The modal for adding users to a team.
 *
 * @param {Props} props - Props
 * @param {Team} props.team - The team we will be adding to.
 * @param {(users: Array<{ id: number; roleId: number }>) => void} props.onAddedUsers - Method for saving the new users to a team.
 * @param {() => void} onCancel - Method for closing the modal/losing changes.
 */
const ModalAddTeamMembers = ({ team, onAddedUsers, onCancel }: Props) => {
  const users = useSelector(UsersModule.selectors.getUserList);
  const status = useSelector(TeamsModule.selectors.getTeamUpdatingStatus);
  const roles = useSelector(RolesModules.selectors.getRoles);

  const [selectedUsers, setSelectedUsers] = useState<Array<SelectOption<number>>>([]);
  const [roleId, setRoleId] = useState<number>(6);

  const userOptions = users.filter(user => (
    user.enabled &&
    !team.teamUsers?.some(teamUser => teamUser.userId === user.id)
  )).map(user => selectOptionFactory(user.id, user.username || user.email));

  const roleSelectOptions = roleOptions(roles, RoleScope.Team);

  return (
    <Modal show onHide={onCancel} className="modal-add-team-members">
      <Modal.Header closeButton>
        <Modal.Title>Add Members to {team.name || 'this team'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {hasError(status) && <ErrorAlert status={status} />}
        {status.loading
          ? (
            <div className="margin-top-lg margin-bottom-lg">
              <Loader />
            </div>
            ) : (
              <>
                <ControlLabel htmlFor="users">Users</ControlLabel>
                <Select
                  isMulti
                  options={userOptions}
                  onChange={(values) => {
                    setSelectedUsers(values);
                  }}
                />
                <ControlLabel htmlFor="defaultRoleId">Role <span className="lighter">(for added users)</span></ControlLabel>
                <Select
                  name="defaultRoleId"
                  options={roleSelectOptions}
                  value={roleSelectOptions.find((opt) => opt.value === roleId)}
                  onChange={(e) => {
                    if (e) {
                      setRoleId(e.value);
                    }
                  }}
                />
              </>
            )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="small"
            onClick={onCancel}
            isDisabled={status.loading}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            isDisabled={status.loading || !selectedUsers.length}
            onClick={() => onAddedUsers(selectedUsers.map(({ value }) => ({
              id: value,
              roleId,
            })))}
          >
            Add
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

const ModalAddTeamMembersContainer = (props: Props) => {
  const dispatch = useDispatch();
  const organization = useSelector(OrganizationsModule.selectors.getCurrentOrganization);

  useOnMount(() => {
    dispatch(RolesModules.actions.fetchAll());
  });

  useEffect(() => {
    if (organization) {
      dispatch(UsersModule.actions.extractFromOrganization(organization));
    }
  }, [dispatch, organization]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <ModalAddTeamMembers {...props} />
  );
};

export default ModalAddTeamMembersContainer;
